import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AppShell } from '../shared/ui/AppShell'
import { FeedContainer } from '../features/posts/ui/FeedContainer'
import { LiveMonitoringPill } from '../features/posts/ui/LiveMonitoringPill'
import { usePostsDashboardController } from '../features/posts/state/usePostsDashboardController'
import { useIntersectionSentinel } from '../shared/hooks/useIntersectionSentinel'
import { useActiveTopic } from '../features/topics/state/useActiveTopic'
import { fetchTopics } from '../features/topics/api/topicsClient'
import { type SocialPlatform, type FeedSort } from '../features/posts/model/contracts'

export function LiveFeedPage() {
  const { t } = useTranslation()
  const showAnalytics = true
  const { topicId } = useActiveTopic()

  const { data: topicsData } = useQuery({
    queryKey: ['topics', { page: 1, limit: 100 }],
    queryFn: ({ signal }) => fetchTopics({ page: 1, limit: 100 }, signal),
    staleTime: 5 * 60 * 1000,
  })

  const topicKeywords = topicsData?.data.find((t) => t.id === topicId)?.keywords ?? []

  const controller = usePostsDashboardController()
  const {
    feed,
    loadMore,
    onPlatformChange,
    onSearchChange,
    onDateRangeChange,
    sentimentFilter,
    onSentimentFilterChange,
    keywordFilter,
    onKeywordFilterChange,
    feedSort,
    onFeedSortChange,
  } = controller

  const onIntersect = useCallback(() => {
    if (!feed.hasNextPage) return
    if (feed.isFetchingNextPage) return
    if (feed.isLoading) return
    loadMore()
  }, [feed.hasNextPage, feed.isFetchingNextPage, feed.isLoading, loadMore])

  const sentinel = useIntersectionSentinel({
    enabled: feed.hasNextPage,
    onIntersect,
    rootMargin: '800px 0px',
  })

  const handlePlatformChange = useCallback(
    (platform: SocialPlatform | 'all') => {
      onPlatformChange(platform)
    },
    [onPlatformChange],
  )

  const handleFeedSortChange = useCallback(
    (sort: FeedSort) => {
      onFeedSortChange(sort)
    },
    [onFeedSortChange],
  )

  return (
    <AppShell showAnalytics={showAnalytics}>
      <div className="mx-auto w-full max-w-[680px] px-6 pt-8 pb-4">
        <div className="flex items-center gap-3">
          <h1 className="m-0 text-2xl font-bold text-foreground">
            {t('feed.title', { defaultValue: 'Live Feed' })}
          </h1>
          <LiveMonitoringPill count={topicKeywords.length} />
        </div>
        <p className="mt-2 text-[15px] text-muted-foreground">
          {t('feed.description', {
            defaultValue: 'Real-time social media monitoring and activity.',
          })}
        </p>
      </div>
      <FeedContainer
        feed={feed}
        onLoadMore={loadMore}
        onPlatformChange={handlePlatformChange}
        onSearch={onSearchChange}
        onDateRangeChange={onDateRangeChange}
        sentimentFilter={sentimentFilter}
        onSentimentFilterChange={onSentimentFilterChange}
        keywordFilter={keywordFilter}
        onKeywordFilterChange={onKeywordFilterChange}
        topicKeywords={topicKeywords}
        feedSort={feedSort}
        onFeedSortChange={handleFeedSortChange}
        sentinelRef={sentinel.ref}
      />
    </AppShell>
  )
}
